import type { CapacityDay, ResourceWorkType, WorkSplitPayload } from '@/api/resourceCenterApi'

// The Resources calendar (z8nrz7f0vy, deck slides 7, 8 and 14). Pure date and
// cell rules; every date is a tenant calendar date, `YYYY-MM-DD`, handled in
// UTC parts so no local timezone can shift a day.

const DAY_MS = 24 * 60 * 60 * 1000

const toUtc = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}
const fromUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10)

export function addDays(iso: string, n: number): string {
  return fromUtc(toUtc(iso) + n * DAY_MS)
}

/** 0 = Sunday … 6 = Saturday. */
export function weekdayOf(iso: string): number {
  return new Date(toUtc(iso)).getUTCDay()
}

/** The Sunday on or before a date — the calendar's weeks start on Sunday, as on the slides. */
export function sundayOf(iso: string): string {
  return addDays(iso, -weekdayOf(iso))
}

/** Today in the viewer's own calendar (the date they read off their wall). */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Two weeks at a time, like the deck's grid. */
export const WINDOW_DAYS = 14

/** How far the slider reaches either side of this week. */
export const SLIDER_WEEKS_BACK = 8
export const SLIDER_WEEKS_AHEAD = 26

/** The window's first day for a slider position (weeks from this week's Sunday). */
export function windowStart(today: string, weekOffset: number): string {
  return addDays(sundayOf(today), weekOffset * 7)
}

export function windowDates(start: string, days = WINDOW_DAYS): string[] {
  return Array.from({ length: days }, (_, i) => addDays(start, i))
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** "10/11" — how the deck writes a date. */
export function monthDay(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${m}/${d}`
}

export function dayHeader(iso: string): { dow: string; md: string; weekend: boolean } {
  const w = weekdayOf(iso)
  return { dow: DOW[w], md: monthDay(iso), weekend: w === 0 || w === 6 }
}

/** "9/27 – 10/10". */
export function windowLabel(start: string, days = WINDOW_DAYS): string {
  return `${monthDay(start)} – ${monthDay(addDays(start, days - 1))}`
}

/** 8 → "8", 7.5 → "7.5", 4.333 → "4.3". */
export function hoursLabel(h: number): string {
  return String(Math.round(h * 10) / 10)
}

export interface CellSegment {
  /** FLEX = Dynamic / no split: an open pool. */
  type: ResourceWorkType | 'FLEX'
  hrs: number
}

export type CellView =
  | { kind: 'not-started' }
  | { kind: 'off' }
  | { kind: 'absent'; code: string | null; shiftCode: string | null }
  | {
      kind: 'on'
      /** What is left once absences are taken off the shift. */
      hours: number
      shiftCode: string | null
      /** A partial absence on the day (TRG 4 h), shown as a corner tag. */
      uaCode: string | null
      uaHrs: number
      segments: CellSegment[]
    }

/** What one calendar cell shows (slide 7: hours, the mini-bar and the shift code). */
export function cellView(d: CapacityDay): CellView {
  if (d.state === 'NOT_STARTED') return { kind: 'not-started' }
  if (d.state === 'OFF') return { kind: 'off' }
  if (d.state === 'UNAVAILABLE') return { kind: 'absent', code: d.uaCode, shiftCode: d.shiftCode }
  const parts: CellSegment[] = [
    { type: 'PROCESS', hrs: d.prHrs },
    { type: 'REVIEW', hrs: d.rvHrs },
    { type: 'QA', hrs: d.qaHrs },
    { type: 'SUPPORT', hrs: d.saHrs },
    { type: 'FLEX', hrs: d.flexHrs },
  ]
  return {
    kind: 'on',
    hours: Math.max(0, d.shiftHrs - d.uaHrs),
    shiftCode: d.shiftCode,
    uaCode: d.uaHrs > 0 ? d.uaCode : null,
    uaHrs: d.uaHrs,
    segments: parts.filter(p => p.hrs > 0),
  }
}

// ─── The split editor ────────────────────────────────────────────────────────

export interface SplitDraft {
  isDynamic: boolean
  processPct: number
  reviewPct: number
  qaPct: number
  supportPct: number
  /** Dynamic only: the work they may be given. */
  workTypes: ResourceWorkType[]
}

/** Why the draft cannot be saved, or null when it can. Mirrors the server's rules. */
export function splitProblem(d: SplitDraft): string | null {
  if (d.isDynamic) {
    return d.workTypes.some(t => t !== 'SUPPORT')
      ? null
      : 'Choose at least one of Process, Review or QA.'
  }
  const parts = [d.processPct, d.reviewPct, d.qaPct, d.supportPct]
  if (parts.some(p => !Number.isInteger(p) || p < 0 || p > 100)) return 'Each part is a whole number from 0 to 100.'
  const total = parts.reduce((a, b) => a + b, 0)
  if (total > 100) return `The split adds up to ${total}%; it cannot exceed 100%.`
  if (total === 0) return 'Give at least one percentage, or make it Dynamic.'
  return null
}

export function splitTotal(d: SplitDraft): number {
  return d.isDynamic ? 0 : d.processPct + d.reviewPct + d.qaPct + d.supportPct
}

/** The request body: a fixed split sends its percentages, a Dynamic one its work types. */
export function splitPayload(d: SplitDraft, effectiveFrom: string): WorkSplitPayload {
  return d.isDynamic
    ? { effectiveFrom, isDynamic: true, workTypes: d.workTypes }
    : {
        effectiveFrom,
        isDynamic: false,
        processPct: d.processPct,
        reviewPct: d.reviewPct,
        qaPct: d.qaPct,
        supportPct: d.supportPct,
      }
}
